import React from 'react';
import { Box, Button, Heading, Link, Spinner, Text, VStack } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@kuow/shared-ui';
import { logger } from '@kuow/core';

const REDIRECT_TO = '/members/profile';

type ExchangeStatus = 'pending' | 'success' | 'error' | 'missing';

export const MagicLinkExchangePage: React.FC = () => {
  const { isAuthenticated, initialized, loginWithMagicToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawToken = searchParams.get('token') ?? searchParams.get('magic_token') ?? '';
  const magicToken = React.useMemo(() => {
    try {
      return decodeURIComponent(rawToken).trim();
    } catch {
      return '';
    }
  }, [rawToken]);

  const [status, setStatus] = React.useState<ExchangeStatus>(
    magicToken.length === 0 ? 'missing' : 'pending',
  );
  const attemptedRef = React.useRef(false);

  React.useEffect(() => {
    if (attemptedRef.current) return;
    if (magicToken.length === 0) return;
    if (initialized && isAuthenticated) return;

    attemptedRef.current = true;
    void (async () => {
      try {
        const ok = await loginWithMagicToken(magicToken, true);
        if (ok) {
          setStatus('success');
          navigate(REDIRECT_TO, { replace: true });
          return;
        }
        setStatus('error');
      } catch (error) {
        logger.error('Magic link exchange failed', { error });
        setStatus('error');
      }
    })();
  }, [magicToken, isAuthenticated, initialized, loginWithMagicToken, navigate]);

  if (initialized && isAuthenticated && status !== 'error') {
    return <Navigate to={REDIRECT_TO} replace />;
  }

  return (
    <>
      <Helmet>
        <title>Signing you in… | KUOW</title>
      </Helmet>
      <Box w="full" py={{ base: 10, md: 16 }} display="flex" flex="1" alignItems="center">
        <Box w="full" maxW="360px" mx="auto" px={{ base: 4, md: 6 }}>
          <Box
            bg="bg"
            borderRadius="l3"
            boxShadow={{ base: 'none', md: 'lg' }}
            p={{ base: 6, md: 6 }}
          >
            <VStack gap={4} align="stretch">
              {status === 'pending' || status === 'success' ? (
                <>
                  <Heading fontSize="2xl" fontWeight="semibold" lineHeight="32px">
                    Signing you in…
                  </Heading>
                  <Box display="flex" justifyContent="center" py="4">
                    <Spinner />
                  </Box>
                  <Text fontSize="xs" textAlign="center">
                    Verifying your sign-in link.
                  </Text>
                </>
              ) : null}

              {status === 'missing' ? (
                <>
                  <Heading fontSize="2xl" fontWeight="semibold" lineHeight="32px">
                    Invalid sign-in link
                  </Heading>
                  <Box
                    bg="red.subtle"
                    border="1px solid"
                    borderColor="red.emphasized"
                    color="red.fg"
                    borderRadius="l2"
                    px="4"
                    py="3"
                  >
                    <Text fontSize="sm" textAlign="center">
                      This link is missing or invalid. Please request a new one.
                    </Text>
                  </Box>
                  <Box display="flex" justifyContent="center">
                    <Button asChild variant="solid" colorPalette="river">
                      <RouterLink to="/sign-in">Back to Sign In</RouterLink>
                    </Button>
                  </Box>
                </>
              ) : null}

              {status === 'error' ? (
                <>
                  <Heading fontSize="2xl" fontWeight="semibold" lineHeight="32px">
                    Sign-in link expired
                  </Heading>
                  <Box
                    bg="red.subtle"
                    border="1px solid"
                    borderColor="red.emphasized"
                    color="red.fg"
                    borderRadius="l2"
                    px="4"
                    py="3"
                  >
                    <Text fontSize="sm" textAlign="center">
                      We couldn't sign you in with this link. It may have expired or already been
                      used.
                    </Text>
                  </Box>
                  <Box display="flex" justifyContent="center">
                    <Button asChild variant="solid" colorPalette="river">
                      <RouterLink to="/sign-in">Back to Sign In</RouterLink>
                    </Button>
                  </Box>
                  <Text fontSize="xs" textAlign="center">
                    Need help?{' '}
                    <Link href="mailto:support@kuow.org" color="river.solid">
                      Contact Support
                    </Link>
                  </Text>
                </>
              ) : null}
            </VStack>
          </Box>
        </Box>
      </Box>
    </>
  );
};
